import React, { useState, useMemo, useEffect, useCallback, useContext } from 'react';
import { Stars, Ticker, ObsFeedback, AiTag } from '../ui';
import { HeaderActionsContext } from './strategyCardContext';
import { useSidePanel } from '../../context/SidePanelContext';
import { STRATEGY_INFO } from '../../data/strategyInfo';
import Strategy4Section from './Strategy4Section';
import { WatchlistsTab } from './PerformanceTab';
import { getTabReports, generateTabReport } from '../../api/ai';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// ── Transform API log response → shape SignalTable expects ──────────────────
// API log: [ { run_date, signals: [{symbol, sector, direction, stars, ...}] } ]
// Target : { bullish: [{name, sector, stars, streak, log[10]}], bearish: [...] }
//           dates: [oldest..newest] — 10 date strings
function transformLogToMock(logEntries) {
  // logEntries are newest-first from API; we want oldest→newest for log[]
  const sorted = [...logEntries].reverse(); // oldest first
  const dates  = sorted.map(e => {
    const d = new Date(e.run_date);
    return d.toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'2-digit' })
             .replace(/ /g, '-');
  });

  // Pad to 10 slots if fewer run dates exist
  while (dates.length < 10) dates.unshift('');
  const paddedEntries = [...sorted];
  while (paddedEntries.length < 10) paddedEntries.unshift({ run_date: '', signals: [] });

  // Build sets: key = `${symbol}::${direction}` present on each slot
  const stockMap = {}; // key → { name, sector, stars (max seen), log[10] }

  paddedEntries.forEach((entry, slotIdx) => {
    (entry.signals || []).forEach(sig => {
      const key = `${sig.symbol}::${sig.direction}`;
      if (!stockMap[key]) {
        stockMap[key] = {
          name:      sig.symbol,
          sector:    sig.sector || 'Other',
          stars:     sig.stars,
          direction: sig.direction,
          log:       Array(10).fill(false),
          confidence: sig.confidence,
          criteria:  sig.criteria,
        };
      }
      stockMap[key].log[slotIdx]  = true;
      // Use highest stars seen across days
      if (sig.stars > stockMap[key].stars) {
        stockMap[key].stars      = sig.stars;
        stockMap[key].confidence = sig.confidence;
        stockMap[key].criteria   = sig.criteria;
      }
    });
  });

  // Compute streak: consecutive True days ending at slot 9 (today)
  Object.values(stockMap).forEach(stock => {
    let streak = 0;
    for (let i = 9; i >= 0; i--) {
      if (stock.log[i]) streak++;
      else break;
    }
    stock.streak = streak;
  });

  const all      = Object.values(stockMap);
  const bullish  = all.filter(s => s.direction === 'long');
  const bearish  = all.filter(s => s.direction === 'short');

  return { bullish, bearish, dates: dates.slice(-10) };
}
// TopBar 44px — sub-tab row sticks flush below main nav bar
const TOP_SUBTAB = 44;
// TopBar 44px + sub-tab row 34px = 78px — strategy headers + table headers freeze here
const TOP_TABLE  = 78;

// ── Signal table — one instance per Bullish / Bearish ─────────────────────────
function SignalTable({ rows, dates, showAll, params }) {
  const [sortCol, setSortCol] = useState('streak');
  const [sortDir, setSortDir] = useState(-1);

  const handleSort = col => {
    if (sortCol===col) setSortDir(d=>-d);
    else { setSortCol(col); setSortDir(-1); }
  };

  const STH = ({ col, children, style={} }) => {
    const active = sortCol===col;
    return (
      <th className={active?'srt':''} style={style} onClick={()=>handleSort(col)}>
        {children}
        <span className="sa">{active?(sortDir>0?'↑':'↓'):'↕'}</span>
      </th>
    );
  };

  const displayed = useMemo(() => {
    // showAll = show stocks that qualified on ANY day in 10D window
    // Today Only = only stocks where log[9] (today) is true
    let r = showAll ? rows : rows.filter(r => r.log[9]);
    return [...r].sort((a,b) => {
      if (sortCol==='name')   return a.name.localeCompare(b.name)*sortDir;
      if (sortCol==='sector') return a.sector.localeCompare(b.sector)*sortDir;
      if (sortCol==='streak') return (b.streak - a.streak)*sortDir;
      if (sortCol==='stars')  return (b.stars - a.stars)*sortDir;
      // date column — sort by whether that day was active
      const ai = a.log[9-sortCol] ? 1 : 0;
      const bi = b.log[9-sortCol] ? 1 : 0;
      return (bi-ai)*sortDir;
    });
  }, [rows, showAll, sortCol, sortDir]);

  if (displayed.length === 0) {
    return (
      <div style={{padding:'14px',textAlign:'center',color:'var(--ink-3)',fontSize:11,
        background:'var(--surface-2)',borderRadius:6,border:'.5px solid var(--hair)'}}>
        No qualifying stocks {showAll ? 'in last 10 days' : 'today'}
      </div>
    );
  }

  return (
    <div style={{fontSize:11,color:'var(--ink-3)',marginBottom:6}}>
      {displayed.length} qualifying · sorted by {sortCol}
    <div className="tw" style={{marginTop:4}}>
      {/* table-layout:fixed + colgroup ensures Bullish and Bearish tables
          share identical column widths so they align vertically */}
      <table style={{tableLayout:'fixed'}}>
        <colgroup>
          <col style={{width:90}}/>  {/* Stock */}
          <col style={{width:64}}/>  {/* Sector */}
          <col style={{width:48}}/>  {/* Streak */}
          <col style={{width:76}}/>  {/* Conf */}
          {dates.map((_,i) => <col key={i} style={{width:36}}/>)}
        </colgroup>
        <thead>
          <tr>
            <STH col="name"   style={{textAlign:'left', width:90,  position:'sticky',left:0,background:'var(--surface-2)',zIndex:3}}>Stock</STH>
            <STH col="sector" style={{textAlign:'left', width:64,  position:'sticky',left:90,background:'var(--surface-2)',zIndex:3}}>Sector</STH>
            <STH col="streak" style={{textAlign:'center',width:48}}>Streak</STH>
            <STH col="stars"  style={{textAlign:'center',width:76}}>Conf.</STH>
            {/* Reversed: today first (index 9→0), oldest last */}
            {[...dates].reverse().map((d,i) => (
              <STH key={d} col={9-i} style={{textAlign:'center',width:36,fontSize:11,
                color: i===0 ? 'var(--amber-ink)' : 'inherit',
                fontWeight: i===0 ? 700 : undefined,
              }}>
                {i===0 ? 'Today' : d}
              </STH>
            ))}
          </tr>
        </thead>
        <tbody>
          {displayed.map((row,ri) => (
            <tr key={ri}>
              <td style={{textAlign:'left',fontWeight:600,minWidth:90,
                position:'sticky',left:0,background:'var(--surface)',zIndex:1}}>
                <Ticker name={row.name} sector={row.sector}/>
              </td>
              <td style={{textAlign:'left',fontSize:11,color:'var(--ink-3)',minWidth:64,
                position:'sticky',left:90,background:'var(--surface)',zIndex:1}}>{row.sector}</td>
              <td style={{textAlign:'center',fontFamily:'var(--font-data)',fontWeight:700,fontSize:11,
                color:row.streak>=4?'var(--up-field-ink)':row.streak>=2?'var(--amber)':'var(--ink-3)'}}>
                {row.streak>0 ? `↑${row.streak}` : '—'}
              </td>
              <td style={{textAlign:'center'}}><Stars n={row.stars}/></td>
              {/* Reversed: log[9]=today first, log[0]=oldest last */}
              {[...row.log].reverse().map((active,i) => (
                <td key={i} style={{
                  textAlign:'center',
                  background: i===0 ? 'rgba(26,74,170,.04)' : 'transparent',
                }}>
                  {active
                    ? <span style={{display:'flex',justifyContent:'center'}}><IconCheck size={14}/></span>
                    : <span style={{color:'var(--ink-3)',fontSize:11}}>──</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </div>
  );
}

// ── Strategy info panel — collapsible ⓘ About section ──────────────────────────
function StrategyInfoPanel({ info }) {
  if (!info) return null;

  const SL = ({ children }) => (
    <div style={{
      fontSize: 11, fontWeight: 700, letterSpacing: '.07em',
      textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 5,
    }}>{children}</div>
  );

  return (
    <div style={{
      background: 'var(--surface-2)',
      border: '.5px solid var(--hair)',
      borderRadius: 6,
      padding: '10px 12px',
      marginBottom: 12,
    }}>
      {/* Schedule — shown first so it's immediately visible */}
      {info.schedule && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 7,
          marginBottom: 10, padding: '6px 8px',
          background: 'rgba(59,130,246,.06)',
          border: '.5px solid rgba(59,130,246,.2)',
          borderRadius: 4,
        }}>
          <span style={{ fontSize: 11, flexShrink: 0 }}>🕓</span>
          <span style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.55 }}>
            {info.schedule}
          </span>
        </div>
      )}

      {/* Principle */}
      <SL>Strategy Principle</SL>
      <div style={{ fontSize: 11, color: 'var(--ink)', lineHeight: 1.65, marginBottom: 12 }}>
        {info.principle}
      </div>

      {/* Entry Conditions */}
      {(info.requiredFilters?.length > 0 || info.confidenceCriteria?.length > 0) && (
        <div style={{ marginBottom: 12 }}>
          <SL>Entry Conditions</SL>
          {info.requiredFilters?.length > 0 && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>
                Required — all must pass
              </div>
              {info.requiredFilters.map((f, i) => (
                <div key={i} style={{ display: 'flex', gap: 7, marginBottom: 3, alignItems: 'flex-start' }}>
                  <span style={{ color: 'var(--up-field-ink)', fontSize: 11, flexShrink: 0, marginTop: 1 }}>✓</span>
                  <span style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.5 }}>{f}</span>
                </div>
              ))}
            </div>
          )}
          {info.confidenceCriteria?.length > 0 && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>
                Confidence scoring (+1 per criterion)
              </div>
              {info.confidenceCriteria.map((c, i) => {
                const [pts, ...rest] = c.split('  ');
                return (
                  <div key={i} style={{ display: 'flex', gap: 7, marginBottom: 2, alignItems: 'flex-start' }}>
                    <span style={{
                      fontFamily: 'var(--font-data)', fontSize: 11,
                      color: 'var(--amber-ink)', flexShrink: 0, marginTop: 2, minWidth: 18,
                    }}>{pts}</span>
                    <span style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.5 }}>{rest.join('  ')}</span>
                  </div>
                );
              })}
              {info.actionableThreshold && (
                <div style={{
                  marginTop: 6, fontSize: 11, color: 'var(--ink-3)',
                  paddingLeft: 8, borderLeft: '2px solid var(--hair-strong)',
                }}>
                  Actionable: {info.actionableThreshold}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* For strategies with no scoring (e.g. Contrarian SIP) — show threshold anyway */}
      {info.confidenceCriteria?.length === 0 && info.actionableThreshold && (
        <div style={{ marginBottom: 12 }}>
          <SL>Selection Rule</SL>
          <div style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.5 }}>
            {info.actionableThreshold}
          </div>
        </div>
      )}

      {/* Research notes */}
      {info.researchNotes?.length > 0 && (
        <div>
          <SL>Research &amp; Variants Tested</SL>
          {info.researchNotes.map((note, i) => (
            <div key={i} style={{ display: 'flex', gap: 7, marginBottom: 3, alignItems: 'flex-start' }}>
              <span style={{ color: 'var(--ink-3)', fontSize: 11, flexShrink: 0 }}>›</span>
              <span style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.5 }}>{note}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Strategy section — contains both Bullish and Bearish sub-tables ────────────
function StrategySection({ strategy, onRunNow, running, loading, showHeader = true }) {
  const [open,        setOpen]       = useState(true);
  const [paramsOpen,  setParamsOpen] = useState(false);
  const [infoOpen,    setInfoOpen]   = useState(false);
  const [params,      setParams]     = useState(strategy.params);

  // When embedded in StrategyCard (showHeader=false), body is always visible
  const isOpen = showHeader ? open : true;

  const s = strategy;
  const info = STRATEGY_INFO.kama_pullback;

  // Embedded in StrategyCard → publish controls to the shared header row.
  const setHeaderActions = useContext(HeaderActionsContext);
  const controlsButtons = (
    <>
      <button className="pill"
        style={{ background: infoOpen ? 'rgba(59,130,246,.18)' : 'rgba(59,130,246,.08)', color:'var(--ink-3)', border:'.5px solid rgba(59,130,246,.28)' }}
        onClick={e=>{e.stopPropagation();setInfoOpen(v=>!v)}}>
        ⓘ About
      </button>
      <button className="pill"
        onClick={e=>{e.stopPropagation();setParamsOpen(p=>!p)}}>
        ⚙ Parameters
      </button>
      <button className="pill on" style={{padding:'4px 12px',fontSize:11}}
        onClick={e=>{e.stopPropagation(); onRunNow && onRunNow();}}
        disabled={running||loading}>
        {running ? '⏳ Running…' : '↺ Run Now'}
      </button>
    </>
  );
  useEffect(() => {
    if (showHeader) return;
    setHeaderActions(controlsButtons);
    return () => setHeaderActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHeader, infoOpen, paramsOpen, running, loading]);

  return (
    <div style={{marginBottom: showHeader ? 16 : 0}}>

      {/* ── STICKY HEADER UNIT ── only rendered when showHeader=true */}
      {showHeader && (
      <div style={{
        position:'sticky', top:TOP_TABLE, zIndex:15,
        background:'var(--paper)',
      }}>
        {/* Name + desc row */}
        <div style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          padding:'8px 12px',
          background:'var(--surface)',
          border:'.5px solid var(--hair)',
          borderBottom: open ? 'none' : '.5px solid var(--hair)',
          borderRadius: open ? '10px 10px 0 0' : 10,
          cursor:'pointer',
        }}
          onClick={()=>setOpen(o=>!o)}>
          <div>
            <div style={{fontWeight:700, fontSize:13, color:'var(--ink)'}}>{s.name}</div>
            <div style={{fontSize:11, color:'var(--ink-3)', marginTop:1}}>{s.desc}</div>
          </div>
          <span style={{color:'var(--ink-3)', fontSize:12, marginLeft:8, flexShrink:0}}>
            {open ? '▲' : '▼'}
          </span>
        </div>

        {/* Controls row */}
        {open && (
          <div style={{
            display:'flex', gap:6, alignItems:'center', flexWrap:'wrap',
            padding:'7px 12px',
            background:'var(--surface)',
            borderLeft:'.5px solid var(--hair)',
            borderRight:'.5px solid var(--hair)',
            borderBottom:'.5px solid var(--hair)',
          }}>
            <button className="pill"
              style={{
                marginLeft:'auto',
                background: infoOpen ? 'rgba(59,130,246,.18)' : 'rgba(59,130,246,.08)',
                color:'var(--ink-3)',
                border:'.5px solid rgba(59,130,246,.28)',
              }}
              onClick={e=>{e.stopPropagation();setInfoOpen(v=>!v)}}>
              ⓘ About
            </button>
            <button className="pill"
              onClick={e=>{e.stopPropagation();setParamsOpen(p=>!p)}}>
              ⚙ Parameters
            </button>
            <button className="pill on" style={{padding:'4px 12px',fontSize:11}}
              onClick={e=>{e.stopPropagation(); onRunNow && onRunNow();}}
              disabled={running||loading}>
              {running ? '⏳ Running…' : '↺ Run Now'}
            </button>
          </div>
        )}
      </div>
      )}
      {/* ── END STICKY HEADER ── */}

      {/* Embedded: controls are published to the StrategyCard header (see useEffect). */}

      {/* Body — scrolls normally on page */}
      {isOpen && (
        <div style={{
          padding: showHeader ? '0 12px 10px' : '0',
          background:'var(--surface)',
          border: showHeader ? '.5px solid var(--hair)' : 'none',
          borderTop:'none',
          borderRadius: showHeader ? '0 0 10px 10px' : 0,
        }}>

          {/* ── Info panel ── */}
          {infoOpen && <StrategyInfoPanel info={info}/>}

          {/* Parameters panel */}
          {paramsOpen && (
            <div style={{background:'var(--surface-2)',border:'.5px solid var(--hair)',borderRadius:6,padding:'10px',marginBottom:12}}>
              <div style={{fontSize:11,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',color:'var(--ink-3)',marginBottom:8}}>
                Confidence Score Parameters
              </div>
              <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:8}}>
                {[
                  {k:'atrThreshold',  l:'ATR/Close min %', unit:'%'},
                  {k:'rangeThreshold',l:'20D range min %',  unit:'%'},
                  {k:'volMultiplier', l:'Volume multiplier',unit:'×'},
                  {k:'rsLookback',    l:'RS lookback days', unit:'D'},
                ].map(p=>(
                  <div key={p.k}>
                    <div style={{fontSize:11,color:'var(--ink-3)',marginBottom:3}}>{p.l}</div>
                    <div style={{display:'flex',alignItems:'center',gap:2,border:'.5px solid var(--hair-strong)',
                      borderRadius:3,background:'var(--ibg)',padding:'2px 6px'}}>
                      <input type="number" value={params[p.k]} step={p.unit==='D'?1:.1}
                        onChange={e=>setParams(pr=>({...pr,[p.k]:+e.target.value}))}
                        style={{width:40,border:'none',background:'transparent',fontSize:11,color:'var(--ink)',outline:'none'}}/>
                      <span style={{fontSize:11,color:'var(--ink-3)'}}>{p.unit}</span>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',gap:6}}>
                <button className="pill on">Save & Re-run</button>
                <button className="pill" onClick={()=>setParams(s.params)}>Reset</button>
              </div>
            </div>
          )}

          {/* ── Section 1: ON THE PLATE NOW (open by default) ── */}
          <CardSection label="On the plate now" defaultOpen>
            <div style={{ marginTop: 10 }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                    padding: '3px 10px', borderRadius: 4,
                    background: 'rgba(26,106,26,.12)', color: 'var(--up-field-ink)', border: '.5px solid rgba(26,106,26,.25)' }}>
                    ▲ Bullish — Long CE setups
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {s.bullish.filter(r => r.log[9]).length} qualifying today
                  </span>
                </div>
                <SignalTable rows={s.bullish} dates={s.dates} showAll={false} params={params}/>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                    padding: '3px 10px', borderRadius: 4,
                    background: 'rgba(136,30,16,.12)', color: 'var(--down-field-ink)', border: '.5px solid rgba(136,30,16,.25)' }}>
                    ▼ Bearish — Long PE setups
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {s.bearish.filter(r => r.log[9]).length} qualifying today
                  </span>
                </div>
                <SignalTable rows={s.bearish} dates={s.dates} showAll={false} params={params}/>
              </div>
            </div>
          </CardSection>

          {/* ── Section 2: History — All 10 days (collapsed) ── */}
          <CardSection label="History — all 10 days">
            <div style={{ marginTop: 10 }}>
              <div style={{ marginBottom: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                    padding: '3px 10px', borderRadius: 4,
                    background: 'rgba(26,106,26,.12)', color: 'var(--up-field-ink)', border: '.5px solid rgba(26,106,26,.25)' }}>
                    ▲ Bullish
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {s.bullish.length} signals in 10D
                  </span>
                </div>
                <SignalTable rows={s.bullish} dates={s.dates} showAll={true} params={params}/>
              </div>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                    padding: '3px 10px', borderRadius: 4,
                    background: 'rgba(136,30,16,.12)', color: 'var(--down-field-ink)', border: '.5px solid rgba(136,30,16,.25)' }}>
                    ▼ Bearish
                  </div>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    {s.bearish.length} signals in 10D
                  </span>
                </div>
                <SignalTable rows={s.bearish} dates={s.dates} showAll={true} params={params}/>
              </div>
            </div>
          </CardSection>

          {/* ── Section 3: Methodology (collapsed) ── */}
          <CardSection label="Methodology">
            <div style={{ marginTop: 8, padding: '8px 10px', background: 'var(--surface-2)', borderRadius: 4,
              fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.8 }}>
              <div>+1 KAMA daily crossed above KAMA weekly within last 20 days</div>
              <div>+1 KAMA cross is clean (no re-crossover in prior 20 days)</div>
              <div>+1 EMA3/EMA9 crossovers ≤ 2 in last 10 days (clean signal)</div>
              <div>+1 ATR(14)/Close &gt; {params.atrThreshold}% (stock moves enough)</div>
              <div>+1 20-day range &gt; {params.rangeThreshold}% (not stuck in zone)</div>
              <div>+1 Volume on crossover day &gt; {params.volMultiplier}× 20-day avg</div>
              <div>+1 RS vs NIFTY positive over {params.rsLookback} days</div>
              <div style={{ marginTop: 6 }}>
                <strong>Stars:</strong> 0–1 pt = ★★ · 2–3 = ★★★ · 4–5 = ★★★★ · 6–7 = ★★★★★
              </div>
            </div>
          </CardSection>

          {/* ── Section 4: Signal Ledger (collapsed) ── */}
          <CardSection label="Signal ledger">
            <SignalLedger strategyId="strat-kama-pullback" archetype="directional" headerless />
          </CardSection>

        </div>
      )}
    </div>
  );
}

// ── Helper: makes stock names in narrative text clickable ─────────────────────
function NarrativeText({ text, stocks }) {
  if (!stocks || stocks.length === 0) return <span>{text}</span>;
  // Build regex from stock names, longest first to avoid partial matches
  const names = [...stocks].sort((a,b) => b.name.length - a.name.length);
  const pattern = new RegExp(`(${names.map(s => s.name.replace(/[-]/g,'\\$&')).join('|')})`, 'g');
  const parts = text.split(pattern);
  return (
    <span>
      {parts.map((part, i) => {
        const stock = names.find(s => s.name === part);
        return stock
          ? <Ticker key={i} name={stock.name} sector={stock.sector}/>
          : <span key={i}>{part}</span>;
      })}
    </span>
  );
}
// Phase 1: mock observations using real pattern logic on mock data.
// Phase 6: these observations generated by Claude API at 16:00 IST daily.

// ── Build observations from scanner data for a given day offset ──────────────
// dayIndex 0 = today, 1 = yesterday, etc.
// Uses log array: log[9-dayIndex] for that day's signal
export function buildObservations(allBullish, allBearish, dayIndex) {
  const logIdx = 9 - dayIndex; // log[9]=today, log[8]=yesterday, etc.
  const dayBullish = allBullish.filter(r => r.log[logIdx]);
  const dayBearish = allBearish.filter(r => r.log[logIdx]);

  const observations = [];

  // High conviction: stars>=4 AND streak>=3 (only meaningful for today; approximate for past)
  const highConviction = dayBullish.filter(r => r.stars >= 4 && r.streak >= 3);
  if (highConviction.length > 0) {
    observations.push({
      type:'High Conviction', color:'#1a6a1a',
      bg:'rgba(26,106,26,.08)', border:'rgba(26,106,26,.2)',
      stocks: highConviction,
      narrative: `${highConviction.map(r=>r.name).join(', ')} — qualifying with ${highConviction[0].streak}+ day streak and ★${highConviction[0].stars} confidence. All filters aligned.`,
      implication: 'Strongest setups of the day. Consider these first if sizing into positions.',
    });
  }

  // Sector cluster: 2+ stocks from same sector
  const sectorCount = {};
  dayBullish.forEach(r => { sectorCount[r.sector] = (sectorCount[r.sector]||0)+1; });
  Object.entries(sectorCount).filter(([,n])=>n>=2).forEach(([sector,count])=>{
    const stocks = dayBullish.filter(r => r.sector===sector);
    observations.push({
      type:'Sector Cluster', color:'#1a4a8a',
      bg:'rgba(26,74,138,.08)', border:'rgba(26,74,138,.2)',
      stocks,
      narrative: `${count} stocks from ${sector} qualifying simultaneously — ${stocks.map(r=>r.name).join(', ')}.`,
      implication: 'Sector-level rotation signal. Watch for index confirmation.',
    });
  });

  // Fresh signal: qualifies today but not yesterday
  if (dayIndex === 0) {
    const freshSignal = allBullish.filter(r => r.log[9] && !r.log[8]);
    if (freshSignal.length > 0) {
      observations.push({
        type:'Fresh Signal', color:'#8a5a00',
        bg:'rgba(138,90,0,.08)', border:'rgba(138,90,0,.2)',
        stocks: freshSignal,
        narrative: `${freshSignal.map(r=>r.name).join(', ')} — signal appeared today after a gap yesterday. Day 1 of a potential new run.`,
        implication: 'Early entry opportunity but unconfirmed. Treat as watchlist until streak builds to 2–3 days.',
      });
    }
  }

  // High frequency: qualified 7+ of 10 days (only shown on today's card)
  if (dayIndex === 0) {
    const highFreq = allBullish.filter(r => r.log.filter(Boolean).length >= 7);
    if (highFreq.length > 0) {
      observations.push({
        type:'High Frequency', color:'#6a1b9a',
        bg:'rgba(106,27,154,.08)', border:'rgba(106,27,154,.2)',
        stocks: highFreq,
        narrative: `${highFreq.map(r=>r.name).join(', ')} — qualified ${highFreq[0].log.filter(Boolean).length}+ of last 10 days. No clean streak but persistent presence.`,
        implication: 'High frequency without clean streak suggests choppy but recurring setup. Consider smaller size or wait for cleaner entry.',
      });
    }
  }

  // Bearish setup
  const persistentBearish = dayBearish.filter(r => r.streak >= 2);
  if (persistentBearish.length > 0) {
    observations.push({
      type:'Bearish Setup', color:'#881e10',
      bg:'rgba(136,30,16,.08)', border:'rgba(136,30,16,.2)',
      stocks: persistentBearish,
      narrative: `${persistentBearish.map(r=>r.name).join(', ')} — qualifying on Bearish scanner with ${persistentBearish[0].streak}+ day streak.`,
      implication: 'Persistent downside momentum. PE setups if market tone stays weak.',
    });
  }

  return { observations, dayBullish, dayBearish };
}

// ── Scanner day card — one collapsible block per day ─────────────────────────
function ScannerDayCard({ dayIndex, dateLabel, defaultOpen, obsId, bullish=[], bearish=[] }) {
  const [open, setOpen] = useState(defaultOpen);
  const { observations, dayBullish, dayBearish } = buildObservations(bullish, bearish, dayIndex);

  return (
    <div className="card" style={{marginBottom:7, overflow:'hidden'}}>
      {/* Day header */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'8px 12px', background:'var(--surface-2)', cursor:'pointer', userSelect:'none'}}
        onClick={()=>setOpen(o=>!o)}>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontWeight:700,fontSize:12,color:'var(--ink)'}}>{dateLabel}</span>
          <span style={{fontSize:11,color:'var(--ink-3)'}}>
            {observations.length} observations · {dayBullish.length} bullish · {dayBearish.length} bearish
          </span>
        </div>
        <span style={{fontSize:11,color:'var(--ink-3)',
          transform:open?'rotate(180deg)':'none',transition:'transform .2s'}}>▼</span>
      </div>

      {open && (
        <div style={{padding:'8px 12px'}}>
          {observations.length === 0 ? (
            <div style={{padding:'12px',textAlign:'center',color:'var(--ink-3)',fontSize:11}}>
              No pattern observations for this day.
            </div>
          ) : observations.map((obs,i) => (
            <div key={i} className="card" style={{marginBottom:8,overflow:'hidden'}}>
              <div style={{padding:'10px 12px'}}>
                <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8,marginBottom:6}}>
                  <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                    <span style={{fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:3,
                      background:obs.bg,color:obs.color,border:`.5px solid ${obs.border}`,
                      textTransform:'uppercase',letterSpacing:'.05em',flexShrink:0}}>
                      {obs.type}
                    </span>
                    <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                      {obs.stocks.map(r=>(
                        <span key={r.name} style={{fontSize:11,fontWeight:600,padding:'1px 6px',borderRadius:3,
                          background:'var(--surface-2)',border:'.5px solid var(--hair)',
                          display:'inline-flex',alignItems:'center',gap:4}}>
                          <Ticker name={r.name} sector={r.sector}/>
                          <span style={{fontSize:11,color:'var(--ink-3)'}}>{'★'.repeat(r.stars)}</span>
                          {r.streak>0&&<span style={{fontSize:11,color:'var(--up-field-ink)'}}>↑{r.streak}</span>}
                        </span>
                      ))}
                    </div>
                  </div>
                  <ObsFeedback obsId={`${obsId}-${dayIndex}-${i}`}/>
                </div>
                <div style={{fontSize:11,color:'var(--ink)',lineHeight:1.55,marginBottom:4}}>
                  <NarrativeText text={obs.narrative} stocks={obs.stocks}/>
                </div>
                <div style={{fontSize:11,color:'var(--ink-2)',lineHeight:1.5,paddingLeft:8,borderLeft:'2px solid var(--hair-strong)'}}>
                  › <NarrativeText text={obs.implication} stocks={obs.stocks}/>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Strategy 2 — Contrarian SIP (NIFTY 100)
// ─────────────────────────────────────────────────────────────────────────────

function fmtRet(r) {
  if (r == null) return '—';
  const pct = (r * 100).toFixed(1);
  return (r >= 0 ? '+' : '') + pct + '%';
}

function fmtPrice(p) {
  if (!p) return '—';
  return '₹' + Number(p).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function fmtDateShort(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch { return iso; }
}

// ── Shared small icon components — CSS circle badges (matches JournalTab style)
function IconCheck({ size=14 }) {
  return (
    <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',
      width:size,height:size,borderRadius:'50%',
      background:'rgba(26,106,26,.15)',color:'var(--up-field-ink)',
      fontWeight:700,fontSize:Math.round(size*0.72),flexShrink:0,lineHeight:1}}>✓</span>
  );
}
function IconWarn({ size=14 }) {
  return (
    <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',
      width:size,height:size,borderRadius:'50%',
      background:'rgba(138,90,0,.15)',color:'#c8922a',
      fontWeight:700,fontSize:Math.round(size*0.72),flexShrink:0,lineHeight:1}}>!</span>
  );
}

// ── 10-Week dot-matrix grid ───────────────────────────────────────────────────
// Always renders 10 columns — pads empty slots when < 10 weeks of data exist

function SipHistoryGrid({ history, latestRunDate, onOpenPanel }) {
  const SLOTS = 10;
  const [sortCol, setSortCol] = useState('symbol');
  const [sortDir, setSortDir] = useState(1);

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => -d);
    else {
      setSortCol(col);
      // week columns sort desc by default (most recent selected first)
      setSortDir(col === 'cap_used_trades' || typeof col === 'number' ? -1 : 1);
    }
  };

  const SortTH = ({ col, children, style={} }) => {
    const active = sortCol === col;
    return (
      <th style={{cursor:'pointer',userSelect:'none',...style}}
        onClick={() => handleSort(col)}>
        {children}
        <span style={{marginLeft:2,opacity:active?1:.3,fontSize:11,
          color:active?'var(--amber-ink)':'var(--ink-3)'}}>
          {active?(sortDir>0?'↑':'↓'):'↕'}
        </span>
      </th>
    );
  };

  const paddedWeeks = useMemo(() => {
    const slots = [...history].slice(0, SLOTS);
    while (slots.length < SLOTS) slots.push(null);
    return slots;
  }, [history]);

  // Build per-symbol meta: sector + rolling 12M trade count
  const symbolMeta = useMemo(() => {
    const meta = {};
    history.forEach(week => {
      [...(week.selected || []), ...(week.skipped_cap || [])].forEach(s => {
        if (!meta[s.symbol]) {
          meta[s.symbol] = {
            sector: s.sector || '—',
            cap_used_trades: s.cap_used_trades ?? 0,
          };
        }
      });
    });
    return meta;
  }, [history]);

  const allSymbols = useMemo(() => {
    const seen = new Set();
    history.forEach(week => {
      [...(week.selected || []), ...(week.skipped_cap || [])].forEach(s => seen.add(s.symbol));
    });
    return [...seen];
  }, [history]);

  // Build lookup: run_date → { symbol → 'selected' | 'skipped' }
  const lookup = useMemo(() => {
    const lk = {};
    history.forEach(week => {
      lk[week.run_date] = {};
      (week.selected    || []).forEach(s => { lk[week.run_date][s.symbol] = 'selected'; });
      (week.skipped_cap || []).forEach(s => { lk[week.run_date][s.symbol] = 'skipped';  });
    });
    return lk;
  }, [history]);

  // Sorted symbols — supports Stock, Sector, 12M, and any week slot (numeric index)
  const sortedSymbols = useMemo(() => {
    return [...allSymbols].sort((a, b) => {
      const ma = symbolMeta[a] || {};
      const mb = symbolMeta[b] || {};
      if (sortCol === 'symbol')          return a.localeCompare(b) * sortDir;
      if (sortCol === 'sector')          return (ma.sector||'').localeCompare(mb.sector||'') * sortDir;
      if (sortCol === 'cap_used_trades') return ((ma.cap_used_trades||0) - (mb.cap_used_trades||0)) * sortDir;
      if (typeof sortCol === 'number') {
        const week = paddedWeeks[sortCol];
        if (!week) return 0;
        const statusA = lookup[week.run_date]?.[a];
        const statusB = lookup[week.run_date]?.[b];
        const rank = s => s==='selected' ? 2 : s==='skipped' ? 1 : 0;
        return (rank(statusB) - rank(statusA)) * sortDir;
      }
      return 0;
    });
  }, [allSymbols, symbolMeta, sortCol, sortDir, paddedWeeks, lookup]);

  if (!history.length) {
    return (
      <div style={{padding:'14px',textAlign:'center',color:'var(--ink-3)',fontSize:11,
        background:'var(--surface-2)',borderRadius:6,border:'.5px solid var(--hair)'}}>
        No history yet. Run the screener first.
      </div>
    );
  }

  return (
    <div>
      <div style={{fontSize:11,color:'var(--ink-3)',marginBottom:4}}>
        {allSymbols.length} stocks appeared in last {history.length} week{history.length!==1?'s':''}
      </div>
      <div className="tw">
        <table>
          <thead>
            <tr>
              <SortTH col="symbol" style={{textAlign:'left',minWidth:100,fontSize:11,
                position:'sticky',left:0,background:'var(--surface-2)',zIndex:3}}>Stock</SortTH>
              <SortTH col="sector" style={{textAlign:'left',minWidth:70,fontSize:11,
                position:'sticky',left:100,background:'var(--surface-2)',zIndex:3}}>Sector</SortTH>
              <SortTH col="cap_used_trades" style={{textAlign:'center',minWidth:46,fontSize:11,
                color:'var(--ink-3)'}}>12M</SortTH>
              {paddedWeeks.map((week, i) => (
                week===null
                  ? <th key={`e${i}`} style={{minWidth:42}}/>
                  : <SortTH key={week.run_date} col={i} style={{textAlign:'center',minWidth:42,fontSize:11,
                      color: i===0 ? 'var(--amber-ink)' : 'var(--ink-3)',
                      fontWeight: i===0 ? 700 : 400}}>
                      {/* week_label is the Monday of the ISO week; falls back to run_date */}
                      {i===0 ? 'This wk' : fmtDateShort(week.week_label || week.run_date)}
                    </SortTH>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedSymbols.map(sym => {
              const meta = symbolMeta[sym] || {};
              const used = meta.cap_used_trades ?? 0;
              return (
                <tr key={sym}>
                  <td style={{textAlign:'left',fontWeight:600,minWidth:100,fontSize:11,
                    position:'sticky',left:0,background:'var(--surface)',zIndex:1}}>
                    <span style={{cursor:'pointer',color:'var(--amber-ink)',
                      borderBottom:'1px dotted var(--amber-ink)'}}
                      onClick={() => onOpenPanel && onOpenPanel({name:sym,sector:''})}>
                      {sym}
                    </span>
                  </td>
                  <td style={{textAlign:'left',minWidth:70,fontSize:11,color:'var(--ink-3)',
                    position:'sticky',left:100,background:'var(--surface)',zIndex:1}}>
                    {meta.sector || '—'}
                  </td>
                  <td style={{textAlign:'center',minWidth:46,fontFamily:'var(--font-data)',
                    fontSize:11,fontWeight:600,
                    color: used>=5 ? 'var(--down-field-ink)' : used>=3 ? '#c8922a' : 'var(--ink-2)'}}>
                    {used} / 5
                  </td>
                  {paddedWeeks.map((week, i) => {
                    const status = week ? lookup[week.run_date]?.[sym] : null;
                    return (
                      <td key={week?.run_date||`e${i}`} style={{textAlign:'center',
                        background: week?.run_date===latestRunDate
                          ? 'rgba(26,74,170,.04)' : 'transparent'}}>
                        {status==='selected'
                          ? <span style={{display:'flex',justifyContent:'center'}}><IconCheck size={14}/></span>
                          : status==='skipped'
                            ? <span style={{display:'flex',justifyContent:'center'}} title="Cap reached"><IconWarn size={14}/></span>
                            : <span style={{color:'var(--ink-3)',fontSize:11}}>──</span>}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div style={{display:'flex',gap:14,marginTop:8,fontSize:11,color:'var(--ink-3)',alignItems:'center'}}>
        <span style={{display:'flex',alignItems:'center',gap:4}}><IconCheck size={14}/> Selected</span>
        <span style={{display:'flex',alignItems:'center',gap:4}}><IconWarn size={14}/> Skipped — cap reached</span>
        <span style={{marginLeft:'auto',fontStyle:'italic'}}>Newest left · oldest right</span>
      </div>
    </div>
  );
}

// ── Per-stock detail card ─────────────────────────────────────────────────────

function SipStockCard({ stock, type, runDate, onOpenPanel, onOverrideCap, overriding }) {
  const isSelected = type === 'selected';
  const isSkipped  = type === 'skipped';
  const capPct     = Math.min(100, (stock.cap_used_trades / 5) * 100);
  const retColor   = stock.return_30d < 0 ? 'var(--down-field-ink)' : 'var(--up-field-ink)';

  return (
    <div style={{
      display:'flex',alignItems:'flex-start',justifyContent:'space-between',
      padding:'6px 0',gap:10,flexWrap:'wrap',
    }}>
      <div style={{display:'flex',alignItems:'flex-start',gap:8,flex:1,minWidth:0}}>
        <div style={{marginTop:2,flexShrink:0}}>
          {isSelected ? <IconCheck size={14}/> : isSkipped ? <IconWarn size={14}/> :
            <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',
              width:14,height:14,borderRadius:'50%',background:'var(--surface-2)',
              color:'var(--ink-3)',fontWeight:700,fontSize:11}}>›</span>}
        </div>
        <div style={{minWidth:0}}>
          {/* Name + return */}
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
            <span style={{fontWeight:700,fontSize:12,color:'var(--amber-ink)',
              cursor:'pointer',borderBottom:'1px dotted var(--amber-ink)'}}
              onClick={() => onOpenPanel && onOpenPanel({name:stock.symbol,sector:''})}>
              {stock.symbol}
            </span>
            <span style={{fontFamily:'var(--font-data)',fontWeight:700,
              fontSize:11,color:retColor}}>
              {fmtRet(stock.return_30d)}
              <span style={{fontWeight:400,fontSize:11,color:'var(--ink-3)',marginLeft:3}}>30D</span>
            </span>
            {type==='next' && (
              <span style={{fontSize:11,color:'var(--ink-3)'}}>Next in queue</span>
            )}
            {stock.cap_override===1 && (
              <span style={{fontSize:11,fontWeight:700,padding:'1px 6px',borderRadius:3,
                background:'rgba(138,90,0,.12)',color:'#c8922a',
                border:'.5px solid rgba(138,90,0,.3)',
                textTransform:'uppercase',letterSpacing:'.04em'}}>
                Cap overridden
              </span>
            )}
          </div>
          {/* Detail row */}
          <div style={{display:'flex',gap:10,marginTop:2,fontSize:11,
            color:'var(--ink-3)',flexWrap:'wrap',alignItems:'center'}}>
            <span>Last: <span style={{fontFamily:'var(--font-data)',color:'var(--ink-2)'}}>
              {fmtPrice(stock.last_price)}
            </span></span>
            <span>·</span>
            <span style={{display:'flex',alignItems:'center',gap:4}}>
              Cap:&nbsp;<span style={{fontFamily:'var(--font-data)',fontWeight:600,
                color:stock.cap_used_trades>=5?'var(--down-field-ink)':'var(--ink-2)'}}>
                {stock.cap_used_trades} / 5
              </span>&nbsp;signals
              <span style={{display:'inline-block',width:36,height:3,
                background:'var(--hair-strong)',borderRadius:2,overflow:'hidden',
                verticalAlign:'middle'}}>
                <span style={{display:'block',height:'100%',borderRadius:2,
                  width:capPct+'%',
                  background:capPct>=100?'var(--down-field-ink)':capPct>=60?'#c8922a':'var(--up-field-ink)',
                  transition:'width .3s'}}/>
              </span>
              <span>({stock.cap_remaining_trades} left)</span>
            </span>
            {isSkipped && stock.reason && (
              <><span>·</span><span style={{color:'var(--down-field-ink)'}}>{stock.reason}</span></>
            )}
          </div>
        </div>
      </div>
      {/* Ignore cap button */}
      {isSkipped && stock.cap_override!==1 && (
        <button className="pill" style={{fontSize:11,padding:'3px 8px',flexShrink:0}}
          disabled={overriding}
          onClick={() => onOverrideCap(stock.symbol, runDate)}>
          {overriding ? '…' : 'Ignore cap'}
        </button>
      )}
    </div>
  );
}

// ── This week's picks — collapsible summary below the grid ───────────────────

function ThisWeekSummary({ selected, skipped, latestRunDate, openPanel, handleOverrideCap, overriding }) {
  const [open, setOpen] = useState(true);
  const all = [...selected.map(s=>({...s,_type:'selected'})), ...skipped.map(s=>({...s,_type:'skipped'}))];

  return (
    <div style={{marginTop:14,borderTop:'.5px solid var(--hair)',paddingTop:10}}>
      {/* Collapsible header */}
      <div style={{display:'flex',alignItems:'center',gap:5,
        cursor:'pointer',userSelect:'none',marginBottom: open ? 8 : 0}}
        onClick={()=>setOpen(o=>!o)}>
        <span style={{fontSize:11,color:'var(--ink-3)',display:'inline-block',lineHeight:1,
          transform:open?'rotate(90deg)':'rotate(0deg)',transition:'transform .15s'}}>▶</span>
        <span style={{fontSize:11,fontWeight:700,letterSpacing:'.07em',
          textTransform:'uppercase',color:'var(--ink-3)'}}>
          This week's picks
        </span>
      </div>

      {open && (
        <div>
          {all.map((s, i) => (
            <div key={s.symbol} style={{
              borderTop: i > 0 ? '.5px solid var(--hair)' : 'none',
              width: 'fit-content', minWidth: '100%',
            }}>
              <SipStockCard
                stock={s}
                type={s._type}
                runDate={latestRunDate}
                onOpenPanel={openPanel}
                onOverrideCap={handleOverrideCap}
                overriding={overriding===s.symbol}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Strategy 2 wrapper section ────────────────────────────────────────────────

function Strategy2Section({ showHeader = true }) {
  const { open: openPanel } = useSidePanel();

  const [open,       setOpen]       = useState(true);
  const [infoOpen,   setInfoOpen]   = useState(false);
  const [data,       setData]       = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [running,    setRunning]    = useState(false);
  const [overriding, setOverriding] = useState(null);
  const [error,      setError]      = useState(null);

  const info = STRATEGY_INFO.contrarian_sip;

  const loadData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await fetch(`${API}/api/scanner/contrarian-sip`);
      const json = await res.json();
      setData(json);
    } catch {
      setError('Could not load Contrarian SIP data. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleRunNow = async () => {
    setRunning(true); setError(null);
    try {
      const res  = await fetch(`${API}/api/scanner/contrarian-sip/run`, { method:'POST' });
      const json = await res.json();
      if (json.status === 'error') setError(json.message || 'Run failed.');
      else setData(json);
    } catch {
      setError('Run failed. Check backend connection.');
    } finally {
      setRunning(false);
    }
  };

  const handleOverrideCap = async (symbol, runDate) => {
    setOverriding(symbol); setError(null);
    try {
      const res  = await fetch(`${API}/api/scanner/contrarian-sip/override-cap`, {
        method:'POST',
        headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ symbol, run_date: runDate }),
      });
      const json = await res.json();
      if (json.status === 'error') setError(json.message || 'Override failed.');
      else setData(json);
    } catch {
      setError('Override failed.');
    } finally {
      setOverriding(null);
    }
  };

  const history      = data?.history       || [];
  const selected     = data?.selected      || [];
  const skipped      = data?.skipped_cap   || [];
  const nextEligible = data?.next_eligible || [];
  const latestRunDate = history[0]?.run_date || null;

  const lastRunLabel = (() => {
    if (loading) return 'Loading…';
    if (!data?.run_at) return 'Not yet run';
    try {
      const d = new Date(data.run_at);
      return 'Last run: ' + d.toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'2-digit'})
        + ' · ' + d.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'});
    } catch { return data.run_at; }
  })();

  const isOpen = showHeader ? open : true;

  // Controls row — reused whether header is shown or not
  // Buttons shared by the standalone controls row and the embedded header slot.
  const controlsButtons = (
    <>
      <button className="pill"
        style={{ background: infoOpen ? 'rgba(59,130,246,.18)' : 'rgba(59,130,246,.08)', color:'var(--ink-3)', border:'.5px solid rgba(59,130,246,.28)' }}
        onClick={e=>{e.stopPropagation();setInfoOpen(v=>!v)}}>
        ⓘ About
      </button>
      <button className="pill on"
        style={{padding:'4px 12px',fontSize:11}}
        onClick={e=>{e.stopPropagation();handleRunNow();}}
        disabled={running||loading}
        title="Runs on Monday · re-uses this week's data if already computed">
        {running ? '⏳ Running…' : '↺ Run Now'}
      </button>
    </>
  );
  const controlsRow = (
    <div style={{
      display:'flex',gap:6,alignItems:'center',flexWrap:'wrap',justifyContent:'flex-end',
      padding:'7px 12px',
      background:'var(--surface)',
      borderLeft: showHeader ? '.5px solid var(--hair)' : 'none',
      borderRight: showHeader ? '.5px solid var(--hair)' : 'none',
      borderBottom:'.5px solid var(--hair)',
    }}>
      {controlsButtons}
    </div>
  );

  // Embedded in StrategyCard → publish buttons to the shared header row.
  const setHeaderActions = useContext(HeaderActionsContext);
  useEffect(() => {
    if (showHeader) return;
    setHeaderActions(controlsButtons);
    return () => setHeaderActions(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showHeader, infoOpen, running, loading]);

  return (
    <div style={{marginBottom: showHeader ? 16 : 0}}>

      {/* ── STICKY HEADER — only when showHeader=true ── */}
      {showHeader && (
      <div style={{position:'sticky',top:TOP_TABLE,zIndex:15,background:'var(--paper)'}}>
        {/* Title row */}
        <div style={{
          display:'flex',alignItems:'center',justifyContent:'space-between',
          padding:'8px 12px', background:'var(--surface)',
          border:'.5px solid var(--hair)',
          borderBottom: open ? 'none' : '.5px solid var(--hair)',
          borderRadius: open ? '10px 10px 0 0' : 10,
          cursor:'pointer',
        }} onClick={() => setOpen(o => !o)}>
          <div>
            <div style={{fontWeight:700,fontSize:13,color:'var(--ink)'}}>Strategy 2 — Contrarian SIP</div>
            <div style={{fontSize:11,color:'var(--ink-3)',marginTop:1}}>NIFTY 100 · 30 trading-day worst performers · ₹10K/stock · max 2/week · equity buy</div>
          </div>
          <span style={{color:'var(--ink-3)',fontSize:12,marginLeft:8,flexShrink:0}}>{open ? '▲' : '▼'}</span>
        </div>
        {open && controlsRow}
      </div>
      )}
      {/* Embedded: controls are published to the StrategyCard header (see useEffect). */}
      {/* ── END STICKY HEADER ── */}

      {/* ── BODY ── */}
      {isOpen && (
        <div style={{
          padding: '0',
          background:'var(--surface)',
          border: showHeader ? '.5px solid var(--hair)' : 'none',
          borderTop:'none',
          borderRadius: showHeader ? '0 0 10px 10px' : 0,
        }}>
          {/* ── Info panel ── */}
          {infoOpen && <StrategyInfoPanel info={info}/>}

          {error && (
            <div style={{marginBottom:10,padding:'8px 12px',
              background:'rgba(136,30,16,.08)',border:'.5px solid rgba(136,30,16,.3)',
              borderRadius:6,fontSize:11,color:'var(--down-field-ink)',
              display:'flex',justifyContent:'space-between'}}>
              {error}
              <button onClick={()=>setError(null)}
                style={{background:'none',border:'none',cursor:'pointer',
                  color:'var(--down-field-ink)',fontSize:13}}>✕</button>
            </div>
          )}

          {loading ? (
            <div style={{padding:'24px',textAlign:'center',color:'var(--ink-3)',fontSize:11}}>
              Loading Contrarian SIP data…
            </div>
          ) : (
            <>
              {/* ── Section 1: On the plate now — this week's picks (open) ── */}
              <CardSection label="On the plate now" defaultOpen>
                <div style={{ padding: '10px 12px 0' }}>
                  {selected.length === 0 && skipped.length === 0 ? (
                    <div style={{ padding: '16px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 11 }}>
                      No picks this week. Run the screener first.
                    </div>
                  ) : (
                    <>
                      {selected.length > 0 && (
                        <div style={{ marginBottom: 12 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700,
                              letterSpacing: '.06em', textTransform: 'uppercase', padding: '3px 10px', borderRadius: 4,
                              background: 'rgba(26,106,26,.12)', color: 'var(--up-field-ink)', border: '.5px solid rgba(26,106,26,.25)' }}>
                              <IconCheck size={13}/> Buy this week
                            </div>
                            <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{selected.length} stock{selected.length > 1 ? 's' : ''}</span>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {selected.map(s => (
                              <SipStockCard key={s.symbol} stock={s} type="selected" runDate={latestRunDate}
                                onOpenPanel={openPanel} onOverrideCap={handleOverrideCap} overriding={overriding === s.symbol}/>
                            ))}
                          </div>
                        </div>
                      )}
                      {skipped.length > 0 && (
                        <div style={{ marginBottom: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                            <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
                              padding: '3px 10px', borderRadius: 4, color: 'var(--ink-3)', border: '.5px solid var(--hair-strong)' }}>
                              Skipped — cap reached
                            </div>
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {skipped.map(s => (
                              <SipStockCard key={s.symbol} stock={s} type="skipped" runDate={latestRunDate}
                                onOpenPanel={openPanel} onOverrideCap={handleOverrideCap} overriding={overriding === s.symbol}/>
                            ))}
                          </div>
                        </div>
                      )}
                      {nextEligible.length > 0 && (
                        <div style={{ marginBottom: 6 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--ink-3)', marginBottom: 6 }}>
                            Next in queue
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {nextEligible.map(s => (
                              <SipStockCard key={s.symbol} stock={s} type="next" runDate={latestRunDate}
                                onOpenPanel={openPanel} onOverrideCap={handleOverrideCap} overriding={overriding === s.symbol}/>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </CardSection>

              {/* ── Section 2: 10-Week History (collapsed) ── */}
              <CardSection label="10-Week history">
                <div style={{ padding: '0 12px' }}>
                  <SipHistoryGrid history={history} latestRunDate={latestRunDate} onOpenPanel={openPanel}/>
                </div>
              </CardSection>

              {/* ── Section 3: Signal Ledger (collapsed) ── */}
              <CardSection label="Signal ledger">
                <SignalLedger strategyId="strat-contrarian-sip" archetype="accumulation" headerless />
              </CardSection>

              {/* Footer */}
              <div style={{ margin: '0 12px', paddingTop: 8, borderTop: '.5px solid var(--hair)',
                fontSize: 11, color: 'var(--ink-3)', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 4 }}>
                <span>Data: yfinance · Universe: {data?.nifty100_source || 'NSE CSV'}</span>
                <span>Cap: strategy signals · contrarian_sip_signals · 5 selected / 12M</span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Scanner AI Report — live Claude-generated observations ───────────────────
// Sub-component so useState is at top level, not inside .map()
function ScannerAIDayCard({ day, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);

  const fmtDate = iso => {
    try { return new Date(iso).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}); }
    catch { return iso; }
  };

  return (
    <div className="card" style={{marginBottom:7,overflow:'hidden'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        padding:'7px 12px',background:'var(--surface-2)',cursor:'pointer',userSelect:'none'}}
        onClick={() => setOpen(o => !o)}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:11,fontWeight:700,color:'var(--ink)'}}>{fmtDate(day.date)}</span>
          <span style={{fontSize:11,color:'var(--ink-3)'}}>{day.obs.length} observations</span>
        </div>
        <span style={{fontSize:11,color:'var(--ink-3)',transform:open?'rotate(180deg)':'none',transition:'transform .2s'}}>▼</span>
      </div>
      {open && (
        <div style={{padding:'0 12px'}}>
          {day.obs.map((obs, oi) => (
            <div key={oi} style={{padding:'8px 0',borderBottom:oi<day.obs.length-1?'.5px solid var(--hair)':'none'}}>
              <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8,marginBottom:5}}>
                <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                  <span style={{fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:3,
                    background:obs.bg||'var(--surface-2)',color:obs.color||'var(--ink)',
                    border:`.5px solid ${obs.border||'var(--hair-strong)'}`,
                    textTransform:'uppercase',letterSpacing:'.05em'}}>
                    {obs.direction==='bullish'?'▲':'▼'} {obs.type}
                  </span>
                  {obs.stocks?.map(s => (
                    <Ticker key={s} name={s} sector=""/>
                  ))}
                </div>
                <ObsFeedback obsId={`scanner-ai-${day.date}-${oi}`} tabSource="scanner" type={obs.type}/>
              </div>
              <div style={{fontSize:11,color:'var(--ink)',lineHeight:1.55,marginBottom:3}}>{obs.text}</div>
              {obs.implication && (
                <div style={{fontSize:11,color:'var(--ink-2)',lineHeight:1.5,paddingLeft:8,borderLeft:'2px solid var(--hair-strong)'}}>
                  › {obs.implication}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ScannerAIReport() {
  const [days,       setDays]       = useState([]);
  const [status,     setStatus]     = useState('loading');
  const [generating, setGenerating] = useState(false);
  const [genMsg,     setGenMsg]     = useState('');

  const loadDays = () => {
    return getTabReports('scanner', 10).then(res => {
      const live = (res.reports || []).map(r => {
        let parsed = {};
        try { parsed = typeof r.report_json === 'string' ? JSON.parse(r.report_json) : r.report_json; } catch {}
        return { date: r.report_date, obs: parsed.observations || [] };
      }).filter(d => d.obs.length > 0);
      setDays(live);
      setStatus(live.length ? 'ok' : 'empty');
      return live;
    });
  };

  useEffect(() => {
    loadDays().catch(() => setStatus('empty'));
  }, []); // eslint-disable-line

  const handleGenerate = async () => {
    setGenerating(true);
    setGenMsg('Generating… (20–60s)');
    const prevDate = days[0]?.date || '';
    try {
      await generateTabReport('scanner', null);
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const res = await getTabReports('scanner', 1);
          const latest = res.reports?.[0];
          if (latest && (attempts > 3 || (latest.report_date || '') !== prevDate)) {
            clearInterval(poll);
            await loadDays();
            setGenerating(false);
            setGenMsg('✓ Generated!');
            setTimeout(() => setGenMsg(''), 4000);
            return;
          }
        } catch {}
        if (attempts >= 25) {
          clearInterval(poll);
          setGenerating(false);
          setGenMsg('⚠ Timed out — refresh in a minute');
        }
      }, 6000);
    } catch (e) {
      setGenerating(false);
      setGenMsg(`Error: ${e.message}`);
    }
  };

  return (
    <div style={{marginBottom:20}}>
      {/* Header + Generate button */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        marginBottom:8,paddingBottom:5,borderBottom:'.5px solid var(--hair)'}}>
        <span style={{fontSize:11,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',color:'#6a1b9a'}}>
          🤖 AI Observations — Nightly Report
        </span>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {genMsg && (
            <span style={{fontSize:11,fontWeight:600,
              color:genMsg.startsWith('✓')?'var(--up-field-ink)':genMsg.startsWith('⚠')?'var(--amber-ink)':'var(--down-field-ink)'}}>
              {genMsg}
            </span>
          )}
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:4,
              border:'.5px solid var(--hair-strong)',background:'var(--surface-2)',color:'var(--ink-2)',
              cursor:generating?'default':'pointer',opacity:generating?0.6:1}}>
            {generating ? '⏳' : '⚡ Generate'}
          </button>
        </div>
      </div>

      {status === 'loading' && (
        <div style={{padding:'16px 0',color:'var(--ink-3)',fontSize:11}}>Loading AI observations…</div>
      )}
      {status === 'empty' && !generating && (
        <div style={{padding:'16px 0',color:'var(--ink-3)',fontSize:11}}>
          No AI observations yet — generates nightly at 00:30 IST or click Generate above.
        </div>
      )}
      {days.map((day, di) => (
        <ScannerAIDayCard key={day.date} day={day} defaultOpen={di === 0}/>
      ))}
    </div>
  );
}

// ScannerAITab — 10-day collapsible log
// ─────────────────────────────────────────────────────────────────────────────
export function ScannerAITab({ mockData }) {
  const s1 = mockData || { bullish: [], bearish: [], dates: Array(10).fill('') };
  const todayBullish = s1.bullish.filter(r=>r.log[9]);
  const todayBearish = s1.bearish.filter(r=>r.log[9]);

  // Generate date labels from real dates array (newest first for display)
  const dayLabels = [...s1.dates].reverse().map((d,i) => d || `Day ${10-i}`);

  return (
    <div>
      {/* Header */}
      <div style={{marginBottom:12}}>
        <div style={{fontSize:12,fontWeight:700,color:'var(--ink)',marginBottom:2}}>
          Scanner AI Summary
        </div>
        <div style={{fontSize:11,color:'var(--ink-3)'}}>
          Nightly AI observations + rule-based signal log
        </div>
      </div>

      {/* Live Claude-generated observations */}
      <ScannerAIReport/>

      {/* Rule-based signal log header */}
      <div style={{fontSize:11,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',
        color:'var(--ink-3)',marginBottom:8,paddingBottom:5,borderBottom:'.5px solid var(--hair)'}}>
        📊 Signal Log — Last 10 Days (Rule-Based)
      </div>

      {/* Today's count summary */}
      <div style={{display:'flex',gap:10,marginBottom:14,flexWrap:'wrap'}}>
        {[
          {l:'Bullish today', v:todayBullish.length, c:'var(--up-field-ink)'},
          {l:'Bearish today', v:todayBearish.length, c:'var(--down-field-ink)'},
          {l:'Total 10D uniq', v:new Set([...s1.bullish,...s1.bearish].filter(r=>r.log.some(Boolean)).map(r=>r.name)).size, c:'var(--ink)'},
        ].map(m=>(
          <div key={m.l} className="card" style={{padding:'6px 12px',display:'flex',gap:8,alignItems:'center'}}>
            <span style={{fontFamily:'var(--font-data)',fontWeight:700,fontSize:16,color:m.c}}>{m.v}</span>
            <span style={{fontSize:11,color:'var(--ink-3)',textTransform:'uppercase',letterSpacing:'.04em'}}>{m.l}</span>
          </div>
        ))}
      </div>

      {/* 10-day collapsible cards */}
      {dayLabels.map((label,i)=>(
        <ScannerDayCard
          key={i}
          dayIndex={i}
          dateLabel={label}
          defaultOpen={i===0}
          obsId="scanner-ai"
          bullish={s1.bullish}
          bearish={s1.bearish}
        />
      ))}

      <div style={{fontSize:11,color:'var(--ink-3)',marginTop:8,padding:'6px 0',borderTop:'.5px solid var(--hair)'}}>
        Phase 6: observations generated by Claude API · improve over time as you rate them
      </div>
    </div>
  );
}

// ── SignalLedger — per-signal decomposition of the health metric ───────────────
// "See details" link toggles this inline below the strategy header.
// Fetches from GET /api/strategies/{id}/signal-ledger on first open.
// Pending signals show "—" for return columns and are excluded from aggregates.
// headerless=true: skip the toggle button (parent header controls visibility)
function SignalLedger({ strategyId, archetype, headerless = false }) {
  const [open, setOpen]       = useState(headerless); // auto-open when embedded headerless
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState(null);
  const [sortCol, setSortCol] = useState('signal_date');
  const [sortDir, setSortDir] = useState(-1); // -1 = DESC (newest first)

  // Fetch on first render when headerless (already open)
  useEffect(() => {
    if (headerless && !data) {
      setLoading(true);
      fetch(`${API}/api/strategies/${strategyId}/signal-ledger`)
        .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
        .then(d => { setData(d); setLoading(false); })
        .catch(e => { setErr(e.message); setLoading(false); });
    }
  }, [headerless, strategyId]); // eslint-disable-line

  const toggle = () => {
    if (!open && !data) {
      setLoading(true);
      fetch(`${API}/api/strategies/${strategyId}/signal-ledger`)
        .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
        .then(d => { setData(d); setLoading(false); })
        .catch(e => { setErr(e.message); setLoading(false); });
    }
    setOpen(o => !o);
  };

  const handleSort = col => {
    if (sortCol === col) setSortDir(d => -d);
    else { setSortCol(col); setSortDir(col === 'signal_date' ? -1 : 1); }
  };

  const sorted = data?.signals ? [...data.signals].sort((a, b) => {
    const av = a[sortCol] ?? '';
    const bv = b[sortCol] ?? '';
    if (av === '' && bv !== '') return 1;
    if (bv === '' && av !== '') return -1;
    return av < bv ? -sortDir : av > bv ? sortDir : 0;
  }) : [];

  const SortTH = ({ col, children, right }) => {
    const active = sortCol === col;
    return (
      <th onClick={() => handleSort(col)} style={{
        cursor: 'pointer', userSelect: 'none', padding: '4px 6px',
        fontSize: 10, fontWeight: 600, color: active ? 'var(--ink)' : 'var(--ink-3)',
        textTransform: 'uppercase', letterSpacing: '.05em',
        textAlign: right ? 'right' : 'left', whiteSpace: 'nowrap',
        borderBottom: '.5px solid var(--hair)',
        background: 'var(--surface-2)',
      }}>
        {children}{active ? (sortDir > 0 ? ' ↑' : ' ↓') : ''}
      </th>
    );
  };

  const fmtRet = v => v == null ? '—' : (v >= 0 ? '+' : '') + v.toFixed(2) + '%';
  const retColor = v => v == null ? 'var(--ink-3)' : v >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)';
  const outcomeColor = tag => ({
    perfect: 'var(--up-field-ink)', correct: 'var(--up-field-ink)',
    mild_miss: '#c8922a', wrong: 'var(--down-field-ink)',
  })[tag] || 'var(--ink-3)';

  const s = data?.summary;

  return (
    <div style={{ borderTop: '.5px solid var(--hair)', background: 'var(--surface)' }}>
      {/* Toggle link — hidden when parent header controls visibility */}
      {!headerless && (
        <div style={{ padding: '6px 12px' }}>
          <button onClick={toggle} style={{
            background: 'none', border: 'none', padding: 0, cursor: 'pointer',
            fontSize: 11, color: 'var(--accent)', fontWeight: 600,
          }}>
            Signal ledger {open ? '↑' : '↓'}
          </button>
        </div>
      )}

      {open && (
        <div style={{ padding: '0 0 12px 0' }}>
          {loading && (
            <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--ink-3)' }}>Loading…</div>
          )}
          {err && (
            <div style={{ padding: '8px 12px', fontSize: 11, color: 'var(--down-field-ink)' }}>{err}</div>
          )}
          {data && (
            <>
              {/* Caption */}
              <div style={{ padding: '0 12px 6px', fontSize: 10, color: 'var(--ink-3)', fontStyle: 'italic' }}>
                {archetype === 'accumulation'
                  ? 'Live positions — entry at pick date, current as of last price sync.'
                  : `Each signal graded close-to-close over the strategy's ${data.horizon_td}td horizon — strategy merit, not executed trades.`}
              </div>

              {/* Summary bar */}
              {archetype === 'accumulation' ? (
                s && s.n_total > 0 && (
                  <div style={{
                    display: 'flex', gap: 16, flexWrap: 'wrap',
                    padding: '6px 12px', margin: '0 12px 8px',
                    background: 'var(--surface-2)', borderRadius: 6,
                    border: '.5px solid var(--hair)',
                  }}>
                    {[
                      ['Picks', s.n_total],
                      ['Mean live', s.mean_live_pct != null ? fmtRet(s.mean_live_pct) : '—'],
                      ['Best', s.best_live_pct != null ? fmtRet(s.best_live_pct) : '—'],
                      ['Worst', s.worst_live_pct != null ? fmtRet(s.worst_live_pct) : '—'],
                      ['Hit rate', s.hit_rate != null ? s.hit_rate + '%' : '—'],
                    ].map(([label, val]) => (
                      <div key={label} style={{ textAlign: 'center', minWidth: 52 }}>
                        <div style={{ fontSize: 9, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
                        <div style={{
                          fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-data)',
                          fontFeatureSettings: '"tnum"',
                          color: label === 'Mean live' ? retColor(parseFloat(val))
                            : label === 'Best' ? 'var(--up-field-ink)'
                            : label === 'Worst' ? 'var(--down-field-ink)'
                            : 'var(--ink)',
                        }}>
                          {val}
                        </div>
                      </div>
                    ))}
                    {s.n_no_price > 0 && (
                      <div style={{ textAlign: 'center', minWidth: 52 }}>
                        <div style={{ fontSize: 9, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>No price</div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', fontFamily: 'var(--font-data)' }}>{s.n_no_price}</div>
                      </div>
                    )}
                  </div>
                )
              ) : (
                <>
                  {s && s.n_resolved > 0 && (
                    <div style={{
                      display: 'flex', gap: 16, flexWrap: 'wrap',
                      padding: '6px 12px', margin: '0 12px 8px',
                      background: 'var(--surface-2)', borderRadius: 6,
                      border: '.5px solid var(--hair)',
                    }}>
                      {[
                        ['n', `${s.n_resolved} resolved`],
                        ['Mean α', s.mean_alpha != null ? fmtRet(s.mean_alpha) : '—'],
                        ['Median α', s.median_alpha != null ? fmtRet(s.median_alpha) : '—'],
                        ['Best', s.best_alpha != null ? fmtRet(s.best_alpha) : '—'],
                        ['Worst', s.worst_alpha != null ? fmtRet(s.worst_alpha) : '—'],
                        ['Hit rate', s.hit_rate != null ? s.hit_rate + '%' : '—'],
                      ].map(([label, val]) => (
                        <div key={label} style={{ textAlign: 'center', minWidth: 52 }}>
                          <div style={{ fontSize: 9, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>{label}</div>
                          <div style={{
                            fontSize: 11, fontWeight: 700, fontFamily: 'var(--font-data)',
                            fontFeatureSettings: '"tnum"',
                            color: label === 'Mean α' || label === 'Median α'
                              ? retColor(parseFloat(val))
                              : label === 'Best' ? 'var(--up-field-ink)'
                              : label === 'Worst' ? 'var(--down-field-ink)'
                              : 'var(--ink)',
                          }}>
                            {val}
                          </div>
                        </div>
                      ))}
                      {s.n_pending > 0 && (
                        <div style={{ textAlign: 'center', minWidth: 52 }}>
                          <div style={{ fontSize: 9, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.04em' }}>Pending</div>
                          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink-3)', fontFamily: 'var(--font-data)' }}>{s.n_pending}</div>
                        </div>
                      )}
                    </div>
                  )}
                  {s && s.n_resolved === 0 && (
                    <div style={{ padding: '4px 12px 8px', fontSize: 11, color: 'var(--ink-3)' }}>
                      {`No resolved signals yet — ${s.n_pending} pending their ${data.horizon_td}td horizon.`}
                    </div>
                  )}
                </>
              )}

              {/* Table — max height 320px, scrolls inside */}
              <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: 320, padding: '0 12px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                  <thead>
                    <tr>
                      <SortTH col="signal_date">Date</SortTH>
                      <SortTH col="symbol">Symbol</SortTH>
                      {archetype === 'directional' && <SortTH col="direction">Dir</SortTH>}
                      <SortTH col="entry_close" right>Entry</SortTH>
                      {archetype === 'accumulation'
                        ? <SortTH col="current_close" right>Current</SortTH>
                        : <SortTH col="exit_close" right>Exit</SortTH>}
                      {archetype === 'accumulation' && <SortTH col="live_return_pct" right>Live %</SortTH>}
                      {archetype === 'accumulation' && <SortTH col="live_return_inr" right>Live ₹</SortTH>}
                      {archetype === 'accumulation' && <SortTH col="days_held" right>Days</SortTH>}
                      {archetype === 'directional' && <SortTH col="signal_return" right>Signal Ret</SortTH>}
                      {archetype === 'directional' && <SortTH col="nifty_return" right>NIFTY Ret</SortTH>}
                      {archetype === 'directional' && <SortTH col="alpha" right>Alpha</SortTH>}
                      <SortTH col="status">Status</SortTH>
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((row, i) => {
                      const isLive    = row.status === 'live';
                      const isPending = row.status === 'pending';
                      const fmtInr = v => v == null ? '—' : (v >= 0 ? '+' : '') + '₹' + Math.abs(Math.round(v)).toLocaleString('en-IN');
                      const fmtPx  = v => v == null ? '—' : '₹' + Number(v).toLocaleString('en-IN', { maximumFractionDigits: 2 });
                      return (
                        <tr key={i} style={{ borderBottom: '.5px solid var(--hair)' }}>
                          <td style={{ padding: '4px 6px', fontSize: 11, textAlign: 'left', color: 'var(--ink-3)', whiteSpace: 'nowrap' }}>
                            {fmtDateShort(row.signal_date)}
                          </td>
                          <td style={{ padding: '4px 6px', fontSize: 11, textAlign: 'left', fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap' }}>
                            {row.symbol}
                          </td>
                          {archetype === 'directional' && (
                            <td style={{ padding: '4px 6px', fontSize: 10, textAlign: 'left', whiteSpace: 'nowrap',
                              color: row.direction === 'long' ? 'var(--up-field-ink)' : 'var(--down-field-ink)',
                              fontWeight: 600 }}>
                              {row.direction === 'long' ? '▲ L' : '▼ S'}
                            </td>
                          )}
                          {/* Entry */}
                          <td style={{ padding: '4px 6px', fontSize: 11, textAlign: 'right',
                            fontFamily: 'var(--font-data)', fontFeatureSettings: '"tnum"', color: 'var(--ink-2)' }}>
                            {fmtPx(row.entry_close)}
                          </td>
                          {/* Current (SIP) or Exit (directional) */}
                          {archetype === 'accumulation' ? (
                            <td style={{ padding: '4px 6px', fontSize: 11, textAlign: 'right',
                              fontFamily: 'var(--font-data)', fontFeatureSettings: '"tnum"',
                              color: row.no_price ? 'var(--ink-3)' : 'var(--ink-2)',
                              fontStyle: row.no_price ? 'italic' : 'normal' }}>
                              {row.no_price ? 'no data' : fmtPx(row.current_close)}
                            </td>
                          ) : (
                            <td style={{ padding: '4px 6px', fontSize: 11, textAlign: 'right',
                              fontFamily: 'var(--font-data)', fontFeatureSettings: '"tnum"', color: 'var(--ink-2)' }}>
                              {isPending ? '—' : fmtPx(row.exit_close)}
                            </td>
                          )}
                          {/* SIP-only: Live % */}
                          {archetype === 'accumulation' && (
                            <td style={{ padding: '4px 6px', fontSize: 11, textAlign: 'right',
                              fontFamily: 'var(--font-data)', fontFeatureSettings: '"tnum"',
                              fontWeight: 600, color: row.no_price ? 'var(--ink-3)' : retColor(row.live_return_pct) }}>
                              {row.no_price ? '—' : fmtRet(row.live_return_pct)}
                            </td>
                          )}
                          {/* SIP-only: Live ₹ */}
                          {archetype === 'accumulation' && (
                            <td style={{ padding: '4px 6px', fontSize: 11, textAlign: 'right',
                              fontFamily: 'var(--font-data)', fontFeatureSettings: '"tnum"',
                              color: row.no_price ? 'var(--ink-3)' : retColor(row.live_return_inr) }}>
                              {row.no_price ? '—' : fmtInr(row.live_return_inr)}
                            </td>
                          )}
                          {/* SIP-only: Days held */}
                          {archetype === 'accumulation' && (
                            <td style={{ padding: '4px 6px', fontSize: 11, textAlign: 'right',
                              fontFamily: 'var(--font-data)', fontFeatureSettings: '"tnum"', color: 'var(--ink-3)' }}>
                              {row.days_held != null ? row.days_held + 'd' : '—'}
                            </td>
                          )}
                          {/* Signal return */}
                          {archetype === 'directional' && (
                            <td style={{ padding: '4px 6px', fontSize: 11, textAlign: 'right',
                              fontFamily: 'var(--font-data)', fontFeatureSettings: '"tnum"',
                              fontWeight: 600, color: isPending ? 'var(--ink-3)' : retColor(row.signal_return) }}>
                              {isPending ? '—' : fmtRet(row.signal_return)}
                            </td>
                          )}
                          {/* NIFTY return (directional only) */}
                          {archetype === 'directional' && (
                            <td style={{ padding: '4px 6px', fontSize: 11, textAlign: 'right',
                              fontFamily: 'var(--font-data)', fontFeatureSettings: '"tnum"', color: 'var(--ink-3)' }}>
                              {isPending ? '—' : fmtRet(row.nifty_return)}
                            </td>
                          )}
                          {/* Alpha (directional) */}
                          {archetype === 'directional' && (
                            <td style={{ padding: '4px 6px', fontSize: 11, textAlign: 'right',
                              fontFamily: 'var(--font-data)', fontFeatureSettings: '"tnum"',
                              fontWeight: 700, color: isPending ? 'var(--ink-3)' : retColor(row.alpha) }}>
                              {isPending ? '—' : fmtRet(row.alpha)}
                            </td>
                          )}
                          {/* Status */}
                          <td style={{ padding: '4px 6px', fontSize: 10, textAlign: 'left', whiteSpace: 'nowrap',
                            color: isPending ? 'var(--ink-3)' : isLive ? 'var(--accent)' : outcomeColor(row.outcome) }}>
                            {isLive ? `live · ${row.days_held}d` : isPending ? 'pending' : row.outcome || 'resolved'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── CardSection — collapsible body section inside expanded strategy card ──────
function CardSection({ label, defaultOpen = false, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: '.5px solid var(--hair)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          width: '100%', padding: '8px 12px', background: 'none', border: 'none',
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.07em' }}>
          {label}
        </span>
        <span style={{ fontSize: 10, color: 'var(--ink-3)', transition: 'transform .15s', transform: open ? 'rotate(180deg)' : 'none' }}>▾</span>
      </button>
      {open && <div style={{ padding: '0 12px 10px' }}>{children}</div>}
    </div>
  );
}

// ── StrategyCard — collapsed triage row + expandable body ─────────────────────
// Lifecycle dot: green = live, amber = calibrating.
// Calibrating rows dim to 75% opacity — signal data exists but outcomes unverified.
// Health metric is archetype-dependent: directional = monthly signal P&L %; accumulation = CAGR.
// Sort in parent by last-signal recency; health is a badge only, NOT the sort key.
function StrategyCard({ name, subtitle, lifecycleState, archetype, signalCell, healthCell, freshness, strategyId, children }) {
  const [expanded, setExpanded]     = useState(false);
  // Action buttons (About / Run Now / Generate …) published by the embedded
  // section via context, rendered on the header row to the right of the stats.
  const [headerActions, setHeaderActions] = useState(null);
  const isLive = lifecycleState === 'live';

  const dotColor   = isLive ? 'var(--up-field-ink)' : 'var(--amber)';
  const healthLabel = archetype === 'accumulation' ? 'CAGR' : 'Mth %';
  const borderRadius = expanded ? '8px 8px 0 0' : 8;

  return (
    <div style={{ marginBottom: 10, opacity: isLive ? 1 : 0.78 }}>
      {/* Triage row */}
      <div
        onClick={() => setExpanded(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10,
          padding: '8px 12px',
          background: 'var(--surface)', border: '.5px solid var(--hair)',
          borderRadius,
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        {/* Lifecycle dot */}
        <span style={{
          width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
          background: dotColor,
          boxShadow: isLive ? `0 0 0 2px rgba(26,106,26,.2)` : `0 0 0 2px rgba(200,146,42,.2)`,
        }}/>

        {/* Name + state + optional subtitle */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {name}
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {isLive ? 'Live' : 'Calibrating'} · {archetype}
          </div>
          {subtitle && (
            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 1, opacity: 0.8 }}>{subtitle}</div>
          )}
        </div>

        {/* Signal cell */}
        <div style={{ textAlign: 'right', minWidth: 64, flexShrink: 0 }}>
          <div style={{ fontSize: 10, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 1 }}>Signal</div>
          <div style={{ fontSize: 12, fontWeight: 700, color: signalCell?.color || 'var(--ink-2)', fontFamily: 'var(--font-data)' }}>
            {signalCell?.value ?? '—'}
          </div>
        </div>

        {/* Health cell — archetype-dependent metric */}
        <div style={{ textAlign: 'right', minWidth: 60, flexShrink: 0 }}>
          <div style={{ fontSize: 10, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 1 }}>
            {healthLabel}
          </div>
          {healthCell?.value != null ? (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: healthCell.color || 'var(--ink-3)', fontFamily: 'var(--font-data)' }}>
                {healthCell.value}
              </div>
              {healthCell.n != null && (
                <div style={{ fontSize: 9, color: 'var(--ink-4)', marginTop: 1 }}>n={healthCell.n}</div>
              )}
            </div>
          ) : healthCell?.chip === 'building' ? (
            <div style={{ textAlign: 'right' }}>
              <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 5px',
                borderRadius: 3, background: 'var(--surface-2)', color: 'var(--ink-3)',
                border: '0.5px solid var(--hair-strong)', letterSpacing: '.04em',
                textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
                Building
              </span>
              <div style={{ fontSize: 9, color: 'var(--ink-4)', marginTop: 1 }}>
                {healthCell.n}/{healthCell.min}
              </div>
            </div>
          ) : lifecycleState === 'calibrating' ? (
            <span style={{ fontSize: 9, fontWeight: 700, padding: '2px 5px',
              borderRadius: 3, background: 'var(--amber-bg)', color: 'var(--amber-ink)',
              border: '0.5px solid var(--amber-hair)', letterSpacing: '.04em',
              textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
              Calibrating
            </span>
          ) : (
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink-4)', fontFamily: 'var(--font-data)' }}>—</div>
          )}
        </div>

        {/* Freshness */}
        <div style={{ fontSize: 11, color: 'var(--ink-3)', minWidth: 52, textAlign: 'right', flexShrink: 0 }}>
          {freshness || '—'}
        </div>

        {/* Header actions (About / Run Now / …) — published by the expanded section */}
        {expanded && headerActions && (
          <div onClick={e => e.stopPropagation()}
            style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            {headerActions}
          </div>
        )}

        {/* Expand toggle */}
        <span style={{ fontSize: 11, color: 'var(--ink-3)', flexShrink: 0, transition: 'transform .15s', transform: expanded ? 'rotate(180deg)' : 'none' }}>
          ▼
        </span>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ border: '.5px solid var(--hair)', borderTop: 'none', borderRadius: '0 0 8px 8px' }}>
          <HeaderActionsContext.Provider value={setHeaderActions}>
            {children}
          </HeaderActionsContext.Provider>
        </div>
      )}
    </div>
  );
}

// ── Cross-Strategy AI tab — second eye, 4-dimension meta-evaluator ─────────────
// HARD RULE: annotate-only (clear | watch | concern). NO score, NO accuracy %, NO win-rate.
// Dimension rubric: portfolio_overlap | regime_fit | event_risk | crowding
const STATUS_STYLE = {
  clear:   { color: 'var(--up-field-ink)',   bg: 'rgba(26,106,26,.10)',   border: 'rgba(26,106,26,.25)',  icon: '✓' },
  watch:   { color: '#c8922a',               bg: 'rgba(200,146,42,.10)',  border: 'rgba(200,146,42,.28)', icon: '!' },
  concern: { color: 'var(--down-field-ink)', bg: 'rgba(136,30,16,.10)',   border: 'rgba(136,30,16,.28)',  icon: '⚑' },
};

const DIMENSION_META = {
  portfolio_overlap: { label: 'Portfolio Overlap', desc: 'Concentration / directional overlap across strategies' },
  regime_fit:        { label: 'Regime Fit',         desc: 'Strategy design vs current market regime' },
  event_risk:        { label: 'Event Risk',          desc: 'Upcoming catalysts threatening open positions' },
  crowding:          { label: 'Crowding / Liquidity', desc: 'Herd-side positioning and liquidity risk' },
};

function DimensionCard({ dimKey, annotation }) {
  const meta  = DIMENSION_META[dimKey];
  const style = STATUS_STYLE[annotation?.status] || STATUS_STYLE.watch;
  if (!meta || !annotation) return null;

  return (
    <div style={{
      marginBottom: 8, padding: '10px 12px',
      background: style.bg, border: `.5px solid ${style.border}`,
      borderRadius: 6,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <span style={{
          fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 3,
          background: style.bg, color: style.color, border: `.5px solid ${style.border}`,
          textTransform: 'uppercase', letterSpacing: '.05em',
        }}>
          {style.icon} {annotation.status}
        </span>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)' }}>{meta.label}</span>
        <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{meta.desc}</span>
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink)', lineHeight: 1.6 }}>
        {annotation.note}
      </div>
    </div>
  );
}

function CrossStrategyDayCard({ day, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);

  const fmtDate = iso => {
    try { return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return iso; }
  };

  const annotations = day.data?.annotations || {};
  const summary     = day.data?.summary || '';
  const dimKeys     = Object.keys(DIMENSION_META);
  const hasConcern  = dimKeys.some(k => annotations[k]?.status === 'concern');
  const hasWatch    = dimKeys.some(k => annotations[k]?.status === 'watch');
  const statusLabel = hasConcern ? '⚑ Concern' : hasWatch ? '! Watch' : '✓ Clear';
  const statusColor = hasConcern ? 'var(--down-field-ink)' : hasWatch ? '#c8922a' : 'var(--up-field-ink)';

  return (
    <div className="card" style={{ marginBottom: 7, overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '7px 12px', background: 'var(--surface-2)', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink)' }}>{fmtDate(day.date)}</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: statusColor }}>{statusLabel}</span>
        </div>
        <span style={{ fontSize: 11, color: 'var(--ink-3)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}>▼</span>
      </div>

      {open && (
        <div style={{ padding: '10px 12px' }}>
          {dimKeys.map(k => <DimensionCard key={k} dimKey={k} annotation={annotations[k]}/>)}
          {summary && (
            <div style={{ marginTop: 8, fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.6,
              paddingLeft: 10, borderLeft: '2px solid var(--hair-strong)' }}>
              {summary}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CrossStrategyAITab() {
  const [days,       setDays]       = useState([]);
  const [status,     setStatus]     = useState('loading');
  const [generating, setGenerating] = useState(false);
  const [genMsg,     setGenMsg]     = useState('');

  const loadDays = () => {
    return getTabReports('cross_strategy', 10).then(res => {
      const live = (res.reports || []).map(r => {
        let parsed = {};
        try { parsed = typeof r.report_json === 'string' ? JSON.parse(r.report_json) : r.report_json; } catch {}
        return { date: r.report_date, data: parsed };
      }).filter(d => d.data?.annotations);
      setDays(live);
      setStatus(live.length ? 'ok' : 'empty');
      return live;
    });
  };

  useEffect(() => {
    loadDays().catch(() => setStatus('empty'));
  }, []); // eslint-disable-line

  const handleGenerate = async () => {
    setGenerating(true);
    setGenMsg('Generating… (20–60s)');
    const prevDate = days[0]?.date || '';
    try {
      await generateTabReport('cross_strategy', null);
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const res = await getTabReports('cross_strategy', 1);
          const latest = res.reports?.[0];
          if (latest && (attempts > 3 || (latest.report_date || '') !== prevDate)) {
            clearInterval(poll);
            await loadDays();
            setGenerating(false);
            setGenMsg('✓ Generated!');
            setTimeout(() => setGenMsg(''), 4000);
            return;
          }
        } catch {}
        if (attempts >= 25) {
          clearInterval(poll);
          setGenerating(false);
          setGenMsg('⚠ Timed out — refresh in a minute');
        }
      }, 6000);
    } catch (e) {
      setGenerating(false);
      setGenMsg(`Error: ${e.message}`);
    }
  };

  return (
    <div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink)', marginBottom: 2 }}>Cross-Strategy AI</div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          Second eye · meta-evaluator across all live strategies · 4-dimension annotation only
        </div>
      </div>

      {/* Generate button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 12, paddingBottom: 8, borderBottom: '.5px solid var(--hair)' }}>
        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
          Opus · annotates portfolio overlap, regime fit, event risk, crowding
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {genMsg && (
            <span style={{ fontSize: 11, fontWeight: 600,
              color: genMsg.startsWith('✓') ? 'var(--up-field-ink)' : genMsg.startsWith('⚠') ? 'var(--amber-ink)' : 'var(--ink-2)' }}>
              {genMsg}
            </span>
          )}
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{ fontSize: 11, fontWeight: 600, padding: '3px 10px', borderRadius: 4,
              border: '.5px solid var(--hair-strong)', background: 'var(--surface-2)', color: 'var(--ink-2)',
              cursor: generating ? 'default' : 'pointer', opacity: generating ? 0.6 : 1 }}>
            {generating ? '⏳' : '⚡ Generate'}
          </button>
        </div>
      </div>

      {status === 'loading' && (
        <div style={{ padding: '16px 0', color: 'var(--ink-3)', fontSize: 11 }}>Loading…</div>
      )}
      {status === 'empty' && !generating && (
        <div style={{ padding: '20px 0', textAlign: 'center', color: 'var(--ink-3)', fontSize: 11 }}>
          <div style={{ marginBottom: 6 }}>No cross-strategy analysis yet.</div>
          <div>Click Generate above to run a portfolio-level second-eye evaluation.</div>
        </div>
      )}
      {days.map((day, di) => (
        <CrossStrategyDayCard key={day.date} day={day} defaultOpen={di === 0}/>
      ))}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
const SCANNER_TABS = [
  {v:'strategies',  l:'Strategies'},
  {v:'watchlists',  l:'Watchlists'},
  {v:'ai',          l:'Cross-Strategy AI'},
];

const EMPTY_PERF_DATA = { rows: [] };

function fmtDateShortLocal(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch { return null; }
}

export default function ScannerTab({ initSub='', onSubChange }) {
  const [sub, setSub] = useState(() => ['strategies','watchlists','ai'].includes(initSub) ? initSub : 'strategies');
  const handleSub = s => { setSub(s); onSubChange && onSubChange(s); };

  // Sync when sidebar navigation changes initSub externally
  useEffect(() => {
    if (initSub && ['strategies','watchlists','ai'].includes(initSub)) setSub(initSub);
  }, [initSub]);

  const [mockData, setMockData] = useState({ bullish: [], bearish: [], dates: Array(10).fill('') });
  const [loading,  setLoading]  = useState(true);
  const [running,  setRunning]  = useState(false);
  const [lastRun,  setLastRun]  = useState(null);  // ISO date for sort
  const [error,    setError]    = useState(null);

  // rawDate = ISO string for sort; freshness = formatted display string
  const [sipMeta, setSipMeta] = useState(null);  // { signal, freshness, rawDate }
  const [s4Meta,  setS4Meta]  = useState(null);  // { signal, freshness, rawDate }
  const [registry, setRegistry] = useState({});
  const [healthData, setHealthData] = useState({});  // { [strategyId]: { metric, label, closed_trades } }

  const loadSignals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(`${API}/api/scanner/signals/log?days=10`);
      const data = await res.json();
      const log  = data.log || [];
      if (log.length > 0) setLastRun(log[0].run_date);
      setMockData(transformLogToMock(log));
    } catch {
      setError('Could not load scanner data. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSignals(); }, [loadSignals]);

  useEffect(() => {
    fetch(`${API}/api/strategies`)
      .then(r => r.json())
      .then(d => {
        const map = {};
        (d.strategies || []).forEach(s => { map[s.id] = s; });
        setRegistry(map);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/api/strategies/health`)
      .then(r => r.json())
      .then(d => setHealthData(d))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/api/scanner/contrarian-sip`)
      .then(r => r.json())
      .then(d => {
        const n = (d.selected || []).length;
        setSipMeta({
          signal:    { value: n > 0 ? `${n} buy${n !== 1 ? 's' : ''}` : '—', color: n > 0 ? 'var(--up-field-ink)' : 'var(--ink-3)' },
          freshness: fmtDateShortLocal(d.run_at),
          rawDate:   d.run_at || null,
        });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch(`${API}/api/strategy4/current`)
      .then(r => r.json())
      .then(d => {
        const pred = d.prediction;
        if (!pred) return;
        const dm = {
          very_bullish: { icon: '▲▲', color: '#22c55e' },
          mild_bullish: { icon: '▲',  color: '#86efac' },
          neutral:      { icon: '—',  color: '#94a3b8' },
          mild_bearish: { icon: '▼',  color: '#fca5a5' },
          very_bearish: { icon: '▼▼', color: '#ef4444' },
        };
        const m = dm[pred.direction] || { icon: pred.direction, color: 'var(--ink-2)' };
        const raw = pred.week_of || pred.generated_at || null;
        setS4Meta({ signal: { value: m.icon, color: m.color }, freshness: fmtDateShortLocal(raw), rawDate: raw });
      })
      .catch(() => {});
  }, []);

  const handleRunNow = async () => {
    setRunning(true);
    setError(null);
    try {
      const res  = await fetch(`${API}/api/scanner/run`, { method: 'POST' });
      const data = await res.json();
      if (data.status === 'error') setError(data.message || 'Scanner run failed.');
      else await loadSignals();
    } catch {
      setError('Scanner run failed. Check backend connection.');
    } finally {
      setRunning(false);
    }
  };

  const strategy1 = {
    ...mockData,
    name:   'Strategy 1 — KAMA Pullback + EMA Trigger',
    desc:   'Far-OTM CE/PE at ~0.25 delta · 21 DTE · Week 1–2 of monthly expiry · 4★+ signals only',
    params: { atrThreshold: 1.5, rangeThreshold: 8, volMultiplier: 1.2, rsLookback: 10 },
  };

  const todayBull  = mockData.bullish.filter(r => r.log[9]).length;
  const todayBear  = mockData.bearish.filter(r => r.log[9]).length;
  const kamaSignal = loading
    ? { value: '…', color: 'var(--ink-3)' }
    : { value: `↑${todayBull} ▼${todayBear}`, color: (todayBull + todayBear) > 0 ? 'var(--ink)' : 'var(--ink-3)' };

  const kamaState = registry['strat-kama-pullback']?.lifecycle_state   || 'calibrating';
  const sipState  = registry['strat-contrarian-sip']?.lifecycle_state  || 'live';
  const s4State   = registry['strat-nifty-debit-spread']?.lifecycle_state || 'calibrating';

  // Sort strategies by most-recent signal date descending; null dates go last
  const strategiesOrdered = useMemo(() => {
    // Returns { value, color } for a computed metric, or { chip } for building/null.
    const makeHealthCell = (stratId) => {
      const h = healthData[stratId];
      if (!h) return null;
      // building chip — enough signals exist but < MIN_RESOLVED outcomes graded yet
      if (h.chip === 'building') return { chip: 'building', n: h.resolved_count, min: h.min_required };
      if (h.metric == null) return null;
      const pct = h.metric;
      const color = pct >= 0 ? 'var(--up-field-ink)' : 'var(--down-field-ink)';
      // Show raw signal return alongside alpha when both available
      const label = h.mean_nifty_return != null
        ? `${pct >= 0 ? '+' : ''}${pct}% α`
        : `${pct >= 0 ? '+' : ''}${pct}%`;
      return { value: label, color, n: h.resolved_count };
    };
    const rows = [
      { key: 's4',   rawDate: s4Meta?.rawDate,  name: 'Strategy #4: Weekly NIFTY AI Debit Spread', subtitle: 'CE spread (current week Tue) + PE hedge 2:5 sizing · Two-step AI: Sonnet backdrop → Opus prediction', lifecycleState: s4State,   archetype: 'directional',  signalCell: s4Meta?.signal,  healthCell: makeHealthCell('strat-nifty-debit-spread'), freshness: s4Meta?.freshness  },
      { key: 'kama', rawDate: lastRun,           name: 'Strategy #1: KAMA Pullback',                subtitle: 'Far-OTM CE/PE at ~0.25 delta · 21 DTE · Week 1–2 of monthly expiry · 4★+ signals only',           lifecycleState: kamaState, archetype: 'directional',  signalCell: kamaSignal,      healthCell: makeHealthCell('strat-kama-pullback'),        freshness: fmtDateShortLocal(lastRun) },
      { key: 'sip',  rawDate: sipMeta?.rawDate,  name: 'Strategy #2: Contrarian SIP',              subtitle: 'NIFTY 100 · 30 trading-day worst performers · ₹10K/stock · max 2/week · equity buy',                          lifecycleState: sipState,  archetype: 'accumulation', signalCell: sipMeta?.signal, healthCell: makeHealthCell('strat-contrarian-sip'),       freshness: sipMeta?.freshness },
    ];
    return [...rows].sort((a, b) => {
      if (!a.rawDate && !b.rawDate) return 0;
      if (!a.rawDate) return 1;
      if (!b.rawDate) return -1;
      return new Date(b.rawDate) - new Date(a.rawDate);
    });
  }, [s4Meta, sipMeta, lastRun, s4State, kamaState, sipState, kamaSignal, healthData]); // eslint-disable-line

  return (
    <div>
      {/* Sub-tab row — topmost, sticks below main nav bar */}
      <div style={{
        position:'sticky', top:TOP_SUBTAB, zIndex:20,
        background:'var(--paper)',
        borderBottom:'.5px solid var(--hair)',
        minHeight:34,
        marginBottom:8,
      }}>
        <div style={{display:'flex',gap:0}}>
          {SCANNER_TABS.map(t => (
            <button key={t.v}
              style={{
                fontSize:11, fontWeight:sub===t.v?700:500,
                padding:'6px 14px', cursor:'pointer',
                color:sub===t.v?'var(--ink)':'var(--ink-2)',
                background:'none', border:'none',
                borderBottom:sub===t.v?'2px solid var(--amber-ink)':'2px solid transparent',
                marginBottom:-1, whiteSpace:'nowrap', transition:'all .15s',
              }}
              onClick={()=>handleSub(t.v)}>
              {t.l}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div style={{marginBottom:8,padding:'8px 12px',background:'rgba(136,30,16,.08)',
          border:'.5px solid rgba(136,30,16,.3)',borderRadius:6,fontSize:11,color:'var(--down-field-ink)'}}>
          {error}
        </div>
      )}

      {sub==='strategies' && (
        <div>
          {strategiesOrdered.map(s => (
            <StrategyCard key={s.key} name={s.name} subtitle={s.subtitle} lifecycleState={s.lifecycleState}
              archetype={s.archetype} signalCell={s.signalCell} healthCell={s.healthCell} freshness={s.freshness}
              strategyId={s.key === 's4' ? 'strat-nifty-debit-spread' : s.key === 'kama' ? 'strat-kama-pullback' : 'strat-contrarian-sip'}>
              {s.key === 's4'   && <Strategy4Section showHeader={false}/>}
              {s.key === 'kama' && <StrategySection showHeader={false} strategy={strategy1} onRunNow={handleRunNow} running={running} loading={loading}/>}
              {s.key === 'sip'  && <Strategy2Section showHeader={false}/>}
            </StrategyCard>
          ))}
        </div>
      )}

      {sub==='watchlists' && (
        <WatchlistsTab
          foData={EMPTY_PERF_DATA}
          niftyCloses={[]}
          symbolUniverse={[]}
          indicesData={EMPTY_PERF_DATA}
          n100Data={EMPTY_PERF_DATA}
        />
      )}

      {sub==='ai' && <CrossStrategyAITab/>}
    </div>
  );
}
